import React, { useMemo, useState } from 'react';
import { Button, Modal, Tooltip } from 'antd';
import QueryBuilder from '../QueryBuilder/QueryBuilder';
import {
  cloneTreeWithFreshIds,
  mergeTreesAsAnd,
  isEmptyTree
} from '../QueryBuilder/treeUtils';

const globalConditionsInfoTooltip = (
  <span>
    Pre-filter for this rule. The rule is skipped entirely when the incoming
    event does not match. Applied at realtime evaluation only — does{' '}
    <strong>not</strong> filter the events that batch precompute aggregates. Use
    the per-function Aggregation Filter for that.
  </span>
);

const FilterConditions = ({
  variantInfo = null,
  typoIndex,
  variantIndex,
  setVariantRenderData,
  isReadOnly,
  onDirtyField,
  disableActions
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [hasExistingTargets, setHasExistingTargets] = useState(false);

  const aggregationFunctions = useMemo(
    () =>
      (variantInfo?.function || [])
        .map((fn, idx) => ({ fn, idx }))
        .filter(({ fn }) => fn.functionName === 'aggregation'),
    [variantInfo?.function]
  );

  const sourceEmpty = isEmptyTree(variantInfo?.condition);
  const noAggregationFunctions = aggregationFunctions.length === 0;
  const buttonDisabled = sourceEmpty || noAggregationFunctions;

  const tooltipMessage = sourceEmpty
    ? 'Set Global Conditions first'
    : noAggregationFunctions
    ? 'No aggregation functions defined for this variant'
    : '';

  const openModal = () => {
    const anyExisting = aggregationFunctions.some(
      ({ fn }) => !isEmptyTree(fn.args?.aggregationCondition)
    );
    setHasExistingTargets(anyExisting);
    setModalOpen(true);
  };

  const applyToAll = (mode) => {
    setVariantRenderData((prev) => {
      const next = prev;
      const variant = next[typoIndex].variants[variantIndex];
      const source = variant.condition;
      aggregationFunctions.forEach(({ idx }) => {
        const targetFn = variant.function[idx];
        const existing = targetFn.args?.aggregationCondition;
        let resultTree;
        if (mode === 'merge' && !isEmptyTree(existing)) {
          resultTree = mergeTreesAsAnd(existing, source);
        } else {
          resultTree = cloneTreeWithFreshIds(source);
        }
        targetFn.args.aggregationCondition = resultTree;
      });
      return [...next];
    });
    onDirtyField();
    setModalOpen(false);
  };

  const buttonNode = (
    <Button
      type="default"
      onClick={openModal}
      disabled={buttonDisabled}
      style={{ marginTop: 12 }}>
      Apply to Aggregation Filters
    </Button>
  );

  return (
    <div>
      <QueryBuilder
        type={`condition`}
        itemNoun="condition"
        typoIndex={typoIndex}
        variantIndex={variantIndex}
        variantInfo={variantInfo}
        isReadOnly={isReadOnly}
        allowNewIfEmpty={true}
        queryValue={variantInfo.condition}
        setVariantRenderData={setVariantRenderData}
        onDirtyField={onDirtyField}
        disableActions={disableActions}
        headerInfo={globalConditionsInfoTooltip}
      />
      {!isReadOnly &&
        (buttonDisabled ? (
          <Tooltip title={tooltipMessage}>
            <span>{buttonNode}</span>
          </Tooltip>
        ) : (
          buttonNode
        ))}
      <Modal
        visible={modalOpen}
        title="Apply Global Conditions to Aggregation Filters"
        onCancel={() => setModalOpen(false)}
        footer={
          hasExistingTargets
            ? [
                <Button key="cancel" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>,
                <Button key="merge" onClick={() => applyToAll('merge')}>
                  Merge (AND)
                </Button>,
                <Button
                  key="replace"
                  type="primary"
                  onClick={() => applyToAll('replace')}>
                  Replace
                </Button>
              ]
            : [
                <Button key="cancel" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>,
                <Button
                  key="apply"
                  type="primary"
                  onClick={() => applyToAll('replace')}>
                  Apply
                </Button>
              ]
        }>
        <p>
          This will copy your Global Conditions to{' '}
          <strong>{aggregationFunctions.length}</strong> aggregation function
          {aggregationFunctions.length === 1 ? '' : 's'}:
        </p>
        <ul>
          {aggregationFunctions.map(({ fn, idx }) => (
            <li key={idx}>{fn.name || `Function ${idx + 1}`}</li>
          ))}
        </ul>
        {hasExistingTargets && (
          <p>
            One or more functions already have an Aggregation Filter. Choose{' '}
            <strong>Replace</strong> to overwrite, or{' '}
            <strong>Merge (AND)</strong> to wrap the existing filter and the
            Global Conditions under a new AND group.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default FilterConditions;
